import { Foot, Masthead } from './Chrome'

/*
  Why Boardium.

  The argument this page makes is the one the product is actually built on, so it is worth
  writing down what it is and what it is not.

  It is: a chat window is stateless and agreeable, and both of those are fatal for the job
  of advising a business. Every session starts from nothing, so the owner does the explaining
  every single time and gets advice shaped by whichever version of the story they told that
  morning. Nothing in the window remembers what it said last month, so it can contradict
  itself without either answer knowing the other exists. Nothing in it has a mandate to
  disagree. And nothing in it ever asks whether you did the thing.

  It is not: a claim that the models are bad. They are the same models. The difference is
  everything around them, which is the part an owner cannot assemble in a browser tab.

  Rules this page keeps, inherited from the homepage header:

  - No headline names an industry, a revenue band or a headcount. The examples are concrete
    and skewed to trades and hospitality because that is the wedge; the claims are not.
  - The technology is named where the argument needs it and nowhere else. This is the one
    page where naming it is the point, because the comparison is the argument, but it still
    never becomes the thing being sold.
  - Every section leads with the failure, then the fix. An owner who has felt the failure
    recognises it in the first sentence; one who has not is being sold to.
*/

interface Panel {
  id: string
  kicker: string
  heading: string
  art: string
  alt: string
  /** The failure, then what replaces it. Two to four paragraphs. */
  body: string[]
  /** One line an owner would actually recognise, pulled out beside the copy. */
  aside?: { quote: string; source: string }
}

const PANELS: Panel[] = [
  {
    id: 'sessions',
    kicker: 'Every session starts at zero',
    heading: 'Stop going round and round.',
    art: 'why-sessions',
    alt: '',
    body: [
      'Open a chat window and the first ten minutes are yours. How many people you carry, '
      + 'what the margins look like, why the second location is a question at all. Then you '
      + 'get an answer, you close the tab, and all of it is gone.',

      'Tomorrow you do it again. And because you are summarising from memory on a day when '
      + 'one thing is annoying you, you describe a slightly different company each time. The '
      + 'advice moves with the description. Not because the model is wrong, but because the '
      + 'question is never the same question twice.',

      'Boardium is told once. The profile, the statements, the documents, the figures and '
      + 'every decision the board has already made stay on file, and every seat reads them '
      + 'before it says anything. You stop being the source of context and go back to being '
      + 'the person asking.',
    ],
  },
  {
    id: 'archive',
    kicker: 'The archive is the product',
    heading: 'It answers from your figures, not from the average of everyone’s.',
    art: 'why-archive',
    alt: '',
    body: [
      '"It depends on your margins" is what you get when nobody knows your margins. It is '
      + 'not wrong. It is just the answer you already had before you asked.',

      'Hand over a profit and loss and the figures come off it and go on file — a text PDF '
      + 'read exactly, a photograph of a printout read by eye. Ask about a pay rise after '
      + 'that and the seat that answers knows what payroll already is as a share of revenue, '
      + 'and says the number back to you.',

      'That is the whole difference between advice about businesses and advice about yours. '
      + 'It is also why the product asks for figures more than it asks for anything else: '
      + 'without them, five seats is an expensive way to get a general answer.',
    ],
    aside: {
      quote: "Don't price this as one raise. Price it as the raise everyone asks for once "
           + "he tells them. On your payroll that's a point of margin.",
      source: 'A seat that had read the payroll line',
    },
  },
  {
    id: 'disagree',
    kicker: 'Somebody has to say no',
    heading: 'A chat window answers the question you asked, in the frame you asked it.',
    art: 'why-dissent',
    alt: '',
    body: [
      'Ask whether to open the second location and you get the considerations for opening a '
      + 'second location. The premise goes unexamined, because nothing in that window has a '
      + 'mandate to examine it. It is a service, and it is being helpful.',

      'A board is not being helpful. Round one goes to five seats at once, each blind to the '
      + 'others, so nobody anchors on the first opinion or on the fact that you clearly want '
      + 'a yes. In round two they see the room and argue, and one seat exists specifically to '
      + 'put the strongest case against wherever the room is heading.',

      'What comes out has a vote count and a named objection attached to it. You can overrule '
      + 'the dissent — most owners do, most of the time — but you do it knowing it was there '
      + 'and who made it. A board that comes back unanimous every week is a mirror.',
    ],
  },
  {
    id: 'reconcile',
    kicker: 'It will not paper over a disagreement',
    heading: "It won't quietly pick a number for you.",
    art: 'why-reconcile',
    alt: '',
    body: [
      'The revenue on your profit and loss and the revenue you typed into the profile in '
      + 'March are both on file. Sometimes they do not match. A summariser averages them, or '
      + 'quietly takes the newer one, and you never learn there was a question.',

      'Boardium groups every figure anyone has asserted by metric and period, and flags any '
      + 'group whose sources differ by more than one percent. It shows you both, and where '
      + 'each came from. You settle it, and that decision is recorded as a claim of its own.',

      'A figure nobody can trace is a figure nobody can argue with. Running a business on a '
      + 'number you never actually chose is worse than not having the number.',
    ],
  },
  {
    id: 'followup',
    kicker: 'It asks whether you did it',
    heading: 'Nothing in a browser tab has ever asked you that.',
    art: 'why-followup',
    alt: '',
    body: [
      'You said you would let the site manager go. Did you? Nobody else in your life asks, '
      + 'because nobody else was in the room when you said it and nobody else wrote down the '
      + 'date.',

      'Every commitment leaves the meeting with an owner, a date and a way to check it. '
      + 'Forty-five days later it comes back onto the agenda by itself and somebody asks. If '
      + 'it slipped, that goes down too, next to what you said at the time.',

      'This is the part a single conversation with a clever stranger structurally cannot do. '
      + 'Not because it is not smart enough, but because it will not be there in April to '
      + 'notice that the thing promised in March did not happen.',
    ],
  },
]

export function Why() {
  return (
    <div className="landing">
      <Masthead />

      <section className="l-section l-why-hero">
        <div className="l-wrap">
          <p className="l-eyebrow">Why Boardium</p>
          <h1 className="l-h1 l-why-hero__h1">
            General advice is everywhere.{' '}
            <em>Advice about your actual numbers isn't.</em>
          </h1>
          <p className="l-lede l-why-hero__lede">
            You can get a good answer out of a chat window today, and it costs nothing. What
            you cannot get is one that knows what your business is, what it said to you last
            quarter, or whether you ever did the thing it suggested.
          </p>
        </div>
      </section>

      {PANELS.map((panel, i) => (
        <section className={`l-section l-why${i % 2 ? ' l-why--flip' : ''}`} id={panel.id} key={panel.id}>
          <div className="l-wrap l-why__grid">
            <div className="l-why__text">
              <p className="l-eyebrow">{panel.kicker}</p>
              <h2 className="l-h2 l-why__h2">{panel.heading}</h2>

              {panel.body.map(p => <p className="l-body l-why__p" key={p.slice(0, 24)}>{p}</p>)}

              {panel.aside && (
                <blockquote className="l-quote l-why__quote">
                  {panel.aside.quote}
                  <cite>{panel.aside.source}</cite>
                </blockquote>
              )}
            </div>

            {/* Decorative. Every one of them restates the paragraph beside it, so a reader
                who cannot see it loses nothing. */}
            <img
              className="l-why__art"
              src={`/illustrations/${panel.art}.webp`}
              srcSet={`/illustrations/${panel.art}.webp 1x, /illustrations/${panel.art}@2x.webp 2x`}
              width={560} height={560} alt="" loading="lazy" decoding="async"
            />
          </div>
        </section>
      ))}

      {/* The honest limit, said before anybody has to discover it. A page arguing this hard
          that never concedes anything reads as a pitch, and it is one paragraph. */}
      <section className="l-section l-band" id="honest">
        <div className="l-wrap l-cols">
          <p className="l-eyebrow l-rail">Said plainly</p>
          <div>
            <h2 className="l-h2">What this isn't.</h2>
            <p className="l-body" style={{ marginTop: 20 }}>
              None of this is a claim that the underlying models are better here than in the
              tab you already have open. They are the same models. What is different is that
              they are pointed at a company they have read, arranged so they have to disagree
              with each other in front of you, and made to come back later and ask. That is
              the part you cannot assemble in a browser tab, and it is the entire product.
            </p>
            <p className="l-body" style={{ marginTop: '1em' }}>
              Nobody on the board is a person, and none of them is an accountant, a lawyer or
              a licensed advisor of any kind. On anything that turns on tax, employment law
              or insurance, the seat answers as well as it can and then says plainly that the
              answer is not a substitute for your attorney or your tax professional.
            </p>
          </div>
        </div>
      </section>

      <section className="l-section l-close">
        <div className="l-wrap">
          <h2 className="l-h2">The board meets every Friday with or without you.</h2>
          <p className="l-lede">Will you be there?</p>
          <div className="l-close__actions">
            <a className="l-btn l-btn--gold l-btn--lg" href="/#signin">Start a board</a>
            <a className="l-btn l-btn--line l-btn--lg" href="/#meeting">Read a full meeting</a>
          </div>
        </div>
      </section>

      <Foot />
    </div>
  )
}
